import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import AION_WOMAN_HERO from "@/assets/aion-public/aion-woman-clean.webp";
import { LOGIN, REGISTER } from "@/constants/testIds/auth";
import {
  getRegistrationStatus,
  loginAccount,
  registerAccount,
  requestPasswordReset,
  resendVerification,
  resetPasswordWithToken,
  verifyEmailToken,
} from "@/services/api";

const defaultRegister = {
  customer_type: "PRIVATE",
  first_name: "",
  last_name: "",
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  country: "Germany",
  city: "",
  address_line_1: "",
  timezone: "Europe/Berlin",
  password: "",
  confirm_password: "",
  accept_privacy: false,
  accept_terms: false,
};

const readAuthLink = () => {
  const hashParams = new URLSearchParams(String(window.location.hash || "").replace(/^#/, ""));
  const queryParams = new URLSearchParams(window.location.search);
  const token = hashParams.get("token") || queryParams.get("token") || "";
  const modeHint = hashParams.get("mode") || queryParams.get("mode") || "";
  const path = window.location.pathname.replace(/\/+$/, "");
  const mode = token
    ? (path === "/auth/reset" ? "reset" : modeHint === "setup" ? "setup" : "verify")
    : "login";
  return { token, mode };
};

const tokenFromPreviewPath = (value) => {
  if (!value) return "";
  const url = new URL(value, window.location.origin);
  const hashParams = new URLSearchParams(String(url.hash || "").replace(/^#/, ""));
  return hashParams.get("token") || url.searchParams.get("token") || "";
};

export const AuthScreen = ({ booting = false, initialError = "", initialMode, onAuthenticated }) => {
  const authLink = useMemo(readAuthLink, []);
  const tokenFromUrl = authLink.token;
  const [mode, setMode] = useState(initialMode || authLink.mode);
  const [loginDraft, setLoginDraft] = useState({ email: "", password: "" });
  const [registerDraft, setRegisterDraft] = useState(defaultRegister);
  const [token, setToken] = useState(tokenFromUrl);
  const [verifyPassword, setVerifyPassword] = useState("");
  const [verifyPasswordConfirm, setVerifyPasswordConfirm] = useState("");
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState(initialError);
  const [pending, setPending] = useState(false);
  const [previewLinks, setPreviewLinks] = useState(null);
  const [registrationStatus, setRegistrationStatus] = useState(null);

  useEffect(() => {
    if (tokenFromUrl) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [tokenFromUrl]);

  useEffect(() => {
    if (!token && ["setup", "reset"].includes(mode)) {
      setMode("login");
    }
  }, [mode, token]);

  useEffect(() => {
    let cancelled = false;
    if (mode !== "register") return undefined;
    getRegistrationStatus()
      .then((status) => {
        if (!cancelled) setRegistrationStatus(status);
      })
      .catch(() => {
        if (!cancelled) setRegistrationStatus(null);
      });
    return () => { cancelled = true; };
  }, [mode]);

  const setModeAndClear = (nextMode) => {
    setMode(nextMode);
    setError("");
    setFeedback("");
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setPending(true);
    setError("");
    try {
      const response = await loginAccount(loginDraft.email, loginDraft.password);
      await onAuthenticated(response);
    } catch (requestError) {
      setError(requestError.message || "Anmeldung fehlgeschlagen.");
    } finally {
      setPending(false);
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setPending(true);
    setError("");
    if (registrationStatus?.registration_enabled === false) {
      setError("Die Registrierung ist gerade noch nicht freigeschaltet, weil die E-Mail-Bestätigung eingerichtet wird. Es wurde kein Konto gespeichert.");
      setPending(false);
      return;
    }
    if (registerDraft.password !== registerDraft.confirm_password) {
      setError("Passwörter stimmen nicht überein.");
      setPending(false);
      return;
    }
    const payload = {
      email: registerDraft.email,
      password: registerDraft.password,
      name: registerDraft.customer_type === "BUSINESS" ? (registerDraft.company_name || registerDraft.contact_name || registerDraft.email) : `${registerDraft.first_name} ${registerDraft.last_name}`.trim(),
      customer_type: registerDraft.customer_type,
      first_name: registerDraft.first_name || undefined,
      last_name: registerDraft.last_name || undefined,
      company_name: registerDraft.customer_type === "BUSINESS" ? registerDraft.company_name : undefined,
      contact_name: registerDraft.contact_name || undefined,
      phone: registerDraft.phone || undefined,
      country: registerDraft.country || undefined,
      city: registerDraft.city || undefined,
      address_line_1: registerDraft.address_line_1 || undefined,
      timezone: registerDraft.timezone,
      accept_privacy: registerDraft.accept_privacy,
      accept_terms: registerDraft.accept_terms,
    };
    try {
      const response = await registerAccount(payload);
      setPreviewLinks(response.preview_links || null);
      setFeedback("Konto erstellt. Bitte bestätige deine E-Mail, bevor du dich anmeldest.");
      setToken("");
      setMode("verify");
    } catch (requestError) {
      setError(requestError.message || "Registrierung fehlgeschlagen.");
    } finally {
      setPending(false);
    }
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    setPending(true);
    setError("");
    if (mode === "setup" && verifyPassword !== verifyPasswordConfirm) {
      setError("Passwörter stimmen nicht überein.");
      setPending(false);
      return;
    }
    try {
      const user = await verifyEmailToken({ token, password: mode === "setup" ? verifyPassword : undefined });
      setFeedback("E-Mail bestätigt. Du kannst dich jetzt anmelden.");
      window.history.replaceState({}, document.title, window.location.pathname);
      setMode("login");
      setLoginDraft((prev) => ({ ...prev, email: user.email || prev.email, password: "" }));
      setToken("");
      setPreviewLinks(null);
    } catch (requestError) {
      setError(requestError.message || "Verifizierung fehlgeschlagen.");
    } finally {
      setPending(false);
    }
  };

  const handleResend = async () => {
    const email = loginDraft.email || registerDraft.email;
    if (!email) {
      setError("Bitte zuerst deine E-Mail-Adresse eingeben.");
      return;
    }
    setPending(true);
    setError("");
    try {
      const response = await resendVerification({ email });
      setPreviewLinks(response.preview_links || null);
      setFeedback("Bestätigungslink wurde erneut vorbereitet.");
      if (response.preview_links?.verify_path) {
        setToken(tokenFromPreviewPath(response.preview_links.verify_path));
        setMode("verify");
      }
    } catch (requestError) {
      setError(requestError.message || "Erneut senden fehlgeschlagen.");
    } finally {
      setPending(false);
    }
  };

  const handleForgot = async (event) => {
    event.preventDefault();
    setPending(true);
    setError("");
    try {
      const response = await requestPasswordReset({ email: loginDraft.email });
      setPreviewLinks(response.preview_links || null);
      setFeedback("Wenn das Konto existiert, wurde ein Reset vorbereitet.");
      if (response.preview_links?.reset_path) {
        setToken(tokenFromPreviewPath(response.preview_links.reset_path));
        setMode("reset");
      }
    } catch (requestError) {
      setError(requestError.message || "Passwort-Reset konnte nicht gestartet werden.");
    } finally {
      setPending(false);
    }
  };

  const handleReset = async (event) => {
    event.preventDefault();
    setPending(true);
    setError("");
    if (verifyPassword !== verifyPasswordConfirm) {
      setError("Passwörter stimmen nicht überein.");
      setPending(false);
      return;
    }
    try {
      await resetPasswordWithToken({ token, password: verifyPassword });
      setFeedback("Neues Passwort gespeichert. Bitte melde dich an.");
      window.history.replaceState({}, document.title, window.location.pathname);
      setMode("login");
      setToken("");
      setVerifyPassword("");
      setVerifyPasswordConfirm("");
    } catch (requestError) {
      setError(requestError.message || "Passwort-Reset fehlgeschlagen.");
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="dashboard-shell" data-testid="admin-login-screen">
      <div className="dashboard-background" />
      <Card className="login-card auth-card" data-testid="public-auth-card">
        <CardHeader className="aion-auth-header">
          <div className="login-logo-stack" data-testid="public-auth-logo-stack">
            <Badge variant="outline" className="w-fit" data-testid="public-auth-badge">AION</Badge>
            <div
              className="aion-auth-premium-hero"
              data-testid="aion-auth-premium-hero"
              style={{ "--aion-auth-portrait": `url("${AION_WOMAN_HERO}")` }}
            >
              <div className="aion-auth-wordmark" data-testid="aion-auth-wordmark">
                <strong>AION</strong>
                <small>Your Life. Smarter.</small>
              </div>
              <div className="aion-auth-orbit" aria-hidden="true"><span/><span/><span/></div>
              <div className="aion-auth-presence-copy">
                <span>{mode === "register" ? "REGISTRIEREN" : mode === "verify" || mode === "setup" ? "E-MAIL BESTÄTIGEN" : mode === "forgot" || mode === "reset" ? "PASSWORT" : "WILLKOMMEN"}</span>
                <strong>AION</strong>
                <small>Dein Zuhause, deine Geräte und deine KI – in einer intelligenten Oberfläche.</small>
              </div>
              <div className="aion-auth-capability-row" aria-label="AION Bereiche">
                <span>Räume</span><span>Energie</span><span>Sicherheit</span><span>Voice</span>
              </div>
            </div>
          </div>
          <CardTitle className="login-title" data-testid="public-auth-title">{mode === "register" ? "Registrieren" : mode === "verify" || mode === "setup" ? "E-Mail bestätigen" : mode === "forgot" || mode === "reset" ? "Passwort sichern" : "Anmelden"}</CardTitle>
          <CardDescription data-testid="login-description">{
            booting
              ? "AION lädt deine letzte sichere Sitzung …"
              : mode === "register"
                ? "Erstelle dein AION Konto und öffne danach Räume, Geräte, Energie, Sicherheit, Voice und Automationen."
                : "Sicher anmelden und dein persönliches AION OS öffnen."
          }</CardDescription>
        </CardHeader>
        <CardContent className="aion-auth-content">
          {feedback ? <div className="aion-note" data-testid="auth-feedback-box">{feedback}</div> : null}
          {error ? <div className="error-box" data-testid="auth-error-box">{error}</div> : null}

          {mode === "login" ? (
            <form className="login-form" onSubmit={handleLogin} data-testid="login-form">
              <label className="login-field" data-testid="login-email-field"><span>E-Mail</span><Input type="email" value={loginDraft.email} onChange={(event) => setLoginDraft((prev) => ({ ...prev, email: event.target.value }))} placeholder="du@aion.app" data-testid="admin-login-email-input" /></label>
              <label className="login-field" data-testid="login-password-field"><span>Passwort</span><Input type="password" value={loginDraft.password} onChange={(event) => setLoginDraft((prev) => ({ ...prev, password: event.target.value }))} placeholder="••••••••••" data-testid="admin-login-password-input" /></label>
              <Button type="submit" disabled={pending || booting} className="login-button" data-testid="admin-login-submit-button">{pending ? "Anmeldung läuft …" : "Anmelden"}</Button>
              <button type="button" className="login-inline-link" onClick={() => setModeAndClear("forgot")} data-testid={LOGIN.forgotPasswordLink}>Passwort vergessen</button>
              <button type="button" className="login-inline-link" onClick={() => void handleResend()} disabled={pending || !loginDraft.email} data-testid="login-resend-verification-button">E-Mail-Bestätigung erneut senden</button>
              <button type="button" className="login-inline-link" onClick={() => setModeAndClear("register")} data-testid={LOGIN.registerLink}>Noch kein Konto? Registrieren</button>
            </form>
          ) : null}

          {mode === "register" ? (
            <form className="login-form" onSubmit={handleRegister} data-testid="register-form">
              {registrationStatus?.registration_enabled === false ? (
                <div className="aion-note" data-testid="registration-unavailable-note">
                  Registrierung wird gerade eingerichtet. Die E-Mail-Bestätigung ist noch nicht aktiv; es wird aktuell kein Konto gespeichert.
                </div>
              ) : null}
              <div className="aion-inline-actions" data-testid="register-type-toggle">
                <Button type="button" variant={registerDraft.customer_type === "PRIVATE" ? "default" : "outline"} onClick={() => setRegisterDraft((prev) => ({ ...prev, customer_type: "PRIVATE" }))} data-testid="register-private-button">Privat</Button>
                <Button type="button" variant={registerDraft.customer_type === "BUSINESS" ? "default" : "outline"} onClick={() => setRegisterDraft((prev) => ({ ...prev, customer_type: "BUSINESS" }))} data-testid="register-business-button">Unternehmen</Button>
              </div>
              {registerDraft.customer_type === "BUSINESS" ? <label className="login-field" data-testid="register-company-field"><span>Firmenname</span><Input value={registerDraft.company_name} onChange={(event) => setRegisterDraft((prev) => ({ ...prev, company_name: event.target.value }))} data-testid="register-company-input" /></label> : null}
              <div className="auth-two-column-grid">
                <label className="login-field" data-testid="register-first-name-field"><span>{registerDraft.customer_type === "BUSINESS" ? "Vorname Kontakt" : "Vorname"}</span><Input value={registerDraft.first_name} onChange={(event) => setRegisterDraft((prev) => ({ ...prev, first_name: event.target.value }))} data-testid="register-first-name-input" /></label>
                <label className="login-field" data-testid="register-last-name-field"><span>{registerDraft.customer_type === "BUSINESS" ? "Nachname Kontakt" : "Nachname"}</span><Input value={registerDraft.last_name} onChange={(event) => setRegisterDraft((prev) => ({ ...prev, last_name: event.target.value }))} data-testid="register-last-name-input" /></label>
              </div>
              {registerDraft.customer_type === "BUSINESS" ? <label className="login-field" data-testid="register-contact-name-field"><span>Ansprechperson</span><Input value={registerDraft.contact_name} onChange={(event) => setRegisterDraft((prev) => ({ ...prev, contact_name: event.target.value }))} data-testid="register-contact-name-input" /></label> : null}
              <label className="login-field" data-testid="register-email-field"><span>E-Mail</span><Input type="email" value={registerDraft.email} onChange={(event) => setRegisterDraft((prev) => ({ ...prev, email: event.target.value }))} data-testid="register-email-input" /></label>
              <label className="login-field" data-testid="register-phone-field"><span>Telefon</span><Input value={registerDraft.phone} onChange={(event) => setRegisterDraft((prev) => ({ ...prev, phone: event.target.value }))} data-testid="register-phone-input" /></label>
              <div className="auth-two-column-grid">
                <label className="login-field" data-testid="register-country-field"><span>Land</span><Input value={registerDraft.country} onChange={(event) => setRegisterDraft((prev) => ({ ...prev, country: event.target.value }))} data-testid="register-country-input" /></label>
                <label className="login-field" data-testid="register-city-field"><span>Stadt</span><Input value={registerDraft.city} onChange={(event) => setRegisterDraft((prev) => ({ ...prev, city: event.target.value }))} data-testid="register-city-input" /></label>
              </div>
              <label className="login-field" data-testid="register-address-field"><span>Adresse</span><Input value={registerDraft.address_line_1} onChange={(event) => setRegisterDraft((prev) => ({ ...prev, address_line_1: event.target.value }))} data-testid="register-address-input" /></label>
              <label className="login-field" data-testid="register-timezone-field"><span>Zeitzone</span><Input value={registerDraft.timezone} onChange={(event) => setRegisterDraft((prev) => ({ ...prev, timezone: event.target.value }))} data-testid="register-timezone-input" /></label>
              <label className="login-field" data-testid="register-password-field"><span>Passwort</span><Input type="password" value={registerDraft.password} onChange={(event) => setRegisterDraft((prev) => ({ ...prev, password: event.target.value }))} data-testid="register-password-input" /></label>
              <label className="login-field" data-testid="register-confirm-password-field"><span>Passwort bestätigen</span><Input type="password" value={registerDraft.confirm_password} onChange={(event) => setRegisterDraft((prev) => ({ ...prev, confirm_password: event.target.value }))} data-testid="register-confirm-password-input" /></label>
              <label className="auth-checkbox-row" data-testid="register-privacy-field"><input type="checkbox" checked={registerDraft.accept_privacy} onChange={(event) => setRegisterDraft((prev) => ({ ...prev, accept_privacy: event.target.checked }))} data-testid="register-privacy-checkbox" /><span>Ich akzeptiere Datenschutz und sichere Verarbeitung.</span></label>
              <label className="auth-checkbox-row" data-testid="register-terms-field"><input type="checkbox" checked={registerDraft.accept_terms} onChange={(event) => setRegisterDraft((prev) => ({ ...prev, accept_terms: event.target.checked }))} data-testid="register-terms-checkbox" /><span>Ich akzeptiere die Nutzungsbedingungen.</span></label>
              <Button type="submit" disabled={pending || registrationStatus?.registration_enabled === false} className="login-button" data-testid={REGISTER.submitButton}>{pending ? "Konto wird erstellt …" : registrationStatus?.registration_enabled === false ? "E-Mail-Verifizierung wird eingerichtet" : "Konto erstellen"}</Button>
              <button type="button" className="login-inline-link" onClick={() => setModeAndClear("login")} data-testid={REGISTER.loginLink}>Schon registriert? Anmelden</button>
            </form>
          ) : null}

          {mode === "verify" || mode === "setup" ? (
            <form className="login-form" onSubmit={handleVerify} data-testid="verify-email-form">
              <label className="login-field" data-testid="verify-token-field"><span>Verifizierungstoken</span><Input value={token} onChange={(event) => setToken(event.target.value)} placeholder="Token" data-testid="verify-token-input" /></label>
              {mode === "setup" ? (
                <>
                  <label className="login-field" data-testid="verify-password-field"><span>Neues Passwort</span><Input type="password" value={verifyPassword} onChange={(event) => setVerifyPassword(event.target.value)} data-testid="verify-password-input" /></label>
                  <label className="login-field" data-testid="verify-password-confirm-field"><span>Passwort bestätigen</span><Input type="password" value={verifyPasswordConfirm} onChange={(event) => setVerifyPasswordConfirm(event.target.value)} data-testid="verify-password-confirm-input" /></label>
                </>
              ) : null}
              {previewLinks?.verify_path || previewLinks?.setup_path ? <div className="aion-note" data-testid="verify-preview-link-box">Preview-Link: {previewLinks.verify_path || previewLinks.setup_path}</div> : null}
              <Button type="submit" disabled={pending || !token} className="login-button" data-testid="verify-email-submit-button">{pending ? "Wird bestätigt …" : "E-Mail bestätigen"}</Button>
              <Button type="button" variant="outline" disabled={pending} onClick={handleResend} data-testid="verify-email-resend-button">Bestätigung erneut senden</Button>
            </form>
          ) : null}

          {mode === "forgot" ? (
            <form className="login-form" onSubmit={handleForgot} data-testid="forgot-password-form">
              <label className="login-field" data-testid="forgot-password-email-field"><span>E-Mail</span><Input type="email" value={loginDraft.email} onChange={(event) => setLoginDraft((prev) => ({ ...prev, email: event.target.value }))} data-testid="forgot-password-email-input" /></label>
              {previewLinks?.reset_path ? <div className="aion-note" data-testid="forgot-password-preview-link-box">Preview link: {previewLinks.reset_path}</div> : null}
              <Button type="submit" disabled={pending} className="login-button" data-testid="forgot-password-submit-button">{pending ? "Wird vorbereitet …" : "Passwort zurücksetzen"}</Button>
              <button type="button" className="login-inline-link" onClick={() => setModeAndClear("login")} data-testid="forgot-password-login-link">Zurück zur Anmeldung</button>
            </form>
          ) : null}

          {mode === "reset" ? (
            <form className="login-form" onSubmit={handleReset} data-testid="reset-password-form">
              <label className="login-field" data-testid="reset-password-token-field"><span>Reset-Token</span><Input value={token} onChange={(event) => setToken(event.target.value)} data-testid="reset-password-token-input" /></label>
              <label className="login-field" data-testid="reset-password-new-field"><span>Neues Passwort</span><Input type="password" value={verifyPassword} onChange={(event) => setVerifyPassword(event.target.value)} data-testid="reset-password-new-input" /></label>
              <label className="login-field" data-testid="reset-password-confirm-field"><span>Passwort bestätigen</span><Input type="password" value={verifyPasswordConfirm} onChange={(event) => setVerifyPasswordConfirm(event.target.value)} data-testid="reset-password-confirm-input" /></label>
              <Button type="submit" disabled={pending || !token} className="login-button" data-testid="reset-password-submit-button">{pending ? "Wird gespeichert …" : "Neues Passwort speichern"}</Button>
            </form>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};